import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLobby } from '@/hooks/useLobby';
import { PlayerStatusInGame } from "@utils/enums";
import { User, Swords } from 'lucide-react';
import MainMenu from '@components/ui/game/main-menu/MainMenu';
import Loader from '@/components/ui/general/loader/Loader';

import './LobbyView.css';

function LobbyView() {
  const navigate = useNavigate();
  const { gameId } = useParams();

  // El hook ahora recibe el callback para manejar el final de la cuenta atrás.
  // Esto es crucial para evitar el error de renderizado.
  const {
    isLoading,
    isStarting,
    error,
    countdown,
    lobbyState,
    players,
    currentUserPlayer,
    isCreator,
    allHumanPlayersReady,
    handleReadyClick,
    handleStartGameClick,
  } = useLobby({
    onCountdownEnd: () => navigate(`/games/${gameId}`)
  });

  // --- Estados de Carga y Error ---
  if (isLoading) {
    return (
      <>
        <MainMenu />
        <Loader message="Loading Lobby..." />
      </>
    );
  }

  if (error) {
    return (
      <>
        <MainMenu />
        <div className="error-message">Error: {error?.message || "Could not load lobby."}</div>
      </>
    );
  }

  // Si lobbyState aún no está disponible, mostramos un loader para evitar errores
  if (!lobbyState) {
    return (
      <>
        <MainMenu />
        <Loader message="Waiting for lobby data..." />
      </>
    );
  }

  const team1 = players.filter(p => p.pivot.team === 1);
  const team2 = players.filter(p => p.pivot.team === 2);

  return (
    <>
      <MainMenu />
      <div className="lobby-container">

        {/* El overlay para la cuenta atrás */}
        {isStarting && (
          <div className="countdown-overlay">
            <div className="countdown-box">
              <Swords size={64} className="countdown-icon" />
              <h1>Game Starting</h1>
              <p className="countdown-timer">{countdown}</p>
            </div>
          </div>
        )}

        <div className="lobby-header">
          <h2 className="lobby-title">{lobbyState.name || `Game #${lobbyState.id}`}</h2>

          {/* Nueva sección de visibilidad */}
          <p>
            Tipo de partida: {lobbyState.visibility === "private" ? "Privada" : "Pública"}
          </p>
          {lobbyState.visibility === "private" && (
            <p>
              Código de acceso: <strong>{lobbyState.access_code}</strong>
            </p>
          )}
        </div>

        <div className="teams-container">
          {/* Columna Equipo 1 */}
          <div className="team-column">
            <h3>Team 1</h3>
            <ul className="player-list">
              {team1.map(player => (
                <li key={player.id} className={`player-card ${player.pivot.status_in_game === PlayerStatusInGame.Lobbied ? 'ready' : ''}`}>
                  <User className="player-icon" size={24} />
                  <span className="player-name">{player.name}</span>
                  {player.id === currentUserPlayer?.id && <span className="you-tag">(You)</span>}
                </li>
              ))}
            </ul>
          </div>

          {/* Separador VS */}
          <div className="vs-separator">
            <span className="vs-text">VS</span>
          </div>

          {/* Columna Equipo 2 */}
          <div className="team-column">
            <h3>Team 2</h3>
            <ul className="player-list">
              {team2.map(player => (
                <li key={player.id} className={`player-card ${player.pivot.status_in_game === PlayerStatusInGame.Lobbied ? 'ready' : ''}`}>
                  <User className="player-icon" size={24} />
                  <span className="player-name">{player.name}</span>
                  {player.id === currentUserPlayer?.id && <span className="you-tag">(You)</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Acciones del Lobby */}
        <div className="lobby-actions">
          
          {isCreator && (
            <button onClick={handleStartGameClick} disabled={!allHumanPlayersReady} className="start-game-button cta">
              Start Game
            </button>
          )}

          {currentUserPlayer && (
            <button onClick={handleReadyClick} className={currentUserPlayer.pivot.status_in_game === PlayerStatusInGame.Lobbied ? 'ready-button' : 'not-ready-button'}>
              {currentUserPlayer.pivot.status_in_game === PlayerStatusInGame.Lobbied ? "I'm Not Ready" : "I'm Ready"}
            </button>
          )}


        </div>

        {!isCreator && <p className="waiting-message">Waiting for the creator to start the game...</p>}
        {isCreator && !allHumanPlayersReady && <p className="waiting-message">Waiting for all players to be ready...</p>}
      </div>
    </>
  );
}

export default LobbyView;